import React, { useCallback, useState } from 'react';
import { Column } from 'primereact/column';
import Table from '../../../../components/Table/Table';
import { formatDate } from '../../../../../utils/formatUtils';
import {
  DataTableRowClickEventParams,
  DataTableRowMouseEventParams,
} from 'primereact/datatable';
import { animateCellText } from '../../../../../utils/htmlUtils';
import {
  ClaimStatus,
  ClaimSummary,
} from '../../../../Interfaces/Insurance/Claim';
import ClaimDrilldown from '../drilldown/ClaimDrilldown';
import TooltipContent from '@/src/app/components/messages/TooltipContent';
import ClaimAllAttachment from './ClaimAllAttachments';
import { ClaimFiltersOptions } from '../ClaimFilters';

type ClaimsTableProps = {
  claims: ClaimSummary[];
  showActiveColumn?: boolean;
  disableHideColumns?: boolean;
  disableDrilldown?: boolean;
  filterStatus: ClaimFiltersOptions['claimStatus'];
  disableEdit?: boolean;
};

const ClaimsTable = React.forwardRef<HTMLDivElement, ClaimsTableProps>(
  (
    {
      claims,
      showActiveColumn,
      disableHideColumns,
      disableDrilldown,
      filterStatus,
      disableEdit,
    },
    ref
  ) => {
    const [showDrilldown, setShowDrilldown] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState<string>();

    const calcHeight = (rows: number) => {
      const headerHeight = 32;
      const footerHeight = 0;
      const rowHeight = 31;
      return headerHeight + footerHeight + rows * rowHeight + 3;
    };

    const onRowHover = (e: DataTableRowMouseEventParams) => {
      const cell = e.originalEvent.target as HTMLElement;
      const row = cell.closest('tr') as HTMLElement;
      animateCellText(row);
    };

    const rowClassName = () => {
      return !disableDrilldown ? 'cursor-pointer' : '';
    };

    const onRowClick = (e: DataTableRowClickEventParams) => {
      if (!disableDrilldown) {
        setSelectedClaim(e.data.id);
        setShowDrilldown(true);
      }
    };
    const rowClickCB = useCallback(onRowClick, []);

    return (
      <div className="pdfDivFitContent">
        {selectedClaim && !disableDrilldown && (
          <ClaimDrilldown
            visible={showDrilldown}
            setVisible={setShowDrilldown}
            claimID={selectedClaim}
            baseUpdateStatus={filterStatus}
            disableEdit={disableEdit}
          />
        )}
        <Table
          id="claims-table"
          ref={ref}
          data={claims}
          className={`mx-3 dashboardOptionShadow noFooter`}
          calcHeight={useCallback(calcHeight, [])}
          rowClassName={useCallback(rowClassName, [])}
          onRowMouseEnter={useCallback(onRowHover, [])}
          onRowClick={rowClickCB}
          hideColumns={!disableHideColumns}
        >
          <Column
            headerClassName={`tableHeader`}
            className="tableCell p-0 printHide"
            footerClassName="tableFooter "
          />
          {showActiveColumn && (
            <Column
              field="status"
              header="Status"
              body={(claim) => (
                <span>
                  <i
                    className={`pi pi-circle-fill printHide ${
                      claim.status === ClaimStatus.Active
                        ? 'text-green-500'
                        : claim.status === ClaimStatus.Closed
                        ? 'errorText'
                        : 'text-yellow-600'
                    }`}
                  />
                  <span className="printMedia printShow capitalize">
                    {claim.status}
                  </span>
                </span>
              )}
              style={{ minWidth: '75px' }}
              headerClassName="tableHeader font-normal justify-content-center text-center"
              className="justify-content-center text-standard blackText tableCell"
              footerClassName="tableFooter"
            />
          )}
          <Column
            field="policy.policy"
            header="Policy"
            body={(claim) => (
              <div className="scroll-text">{claim.policy.policy}</div>
            )}
            headerClassName={`tableHeader font-normal justify-content-center`}
            style={{ minWidth: '200px', maxWidth: '200px' }}
            className={`text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow`}
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="employee.name"
            header="Party Involved"
            body={(claim) => (
              <div className="scroll-text">
                {claim.employee?.name ??
                  claim.subcontractor?.name ??
                  claim.other}
              </div>
            )}
            headerClassName={`tableHeader font-normal justify-content-center`}
            style={{ minWidth: '200px', maxWidth: '200px' }}
            className={`text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow`}
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="type"
            header="Type"
            body={(claim) => <div className="scroll-text">{claim.type}</div>}
            headerClassName={`tableHeader font-normal justify-content-center`}
            style={{ minWidth: '150px', maxWidth: '150px' }}
            className={`text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow`}
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="number"
            header="Policy Number"
            style={{ minWidth: '200px' }}
            headerClassName="tableHeader font-normal justify-content-center text-center"
            className="justify-content-center text-standard blackText tableCell"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            field="contacts[0].name"
            header="Ins Contact Info"
            body={(claim) => (
              <div className="scroll-text">
                {(claim.contacts ?? [])[0]?.name}
              </div>
            )}
            headerClassName={`tableHeader font-normal justify-content-center`}
            style={{ minWidth: '180px', maxWidth: '180px' }}
            className={`text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow`}
            footerClassName="tableFooter"
            sortable
          />

          <Column
            field="accidentDate"
            header="Date of Accident"
            style={{ minWidth: '160px' }}
            body={(claim) => {
              return formatDate(claim.accidentDate);
            }}
            headerClassName="tableHeader font-normal"
            className="justify-content-center text-standard blackText tableCell"
            footerClassName="tableFooter"
            sortable
          />
          <Column
            style={{ minWidth: '40px', maxWidth: '40px' }}
            body={(claim) =>
              claim.hasAttachment ? (
                <ClaimAllAttachment claimID={claim.id} />
              ) : (
                <div className="text-red-600 font-bold">
                  <TooltipContent
                    label="!"
                    content={`No attachment file was found!`}
                    id={claim.id.replace(/-/g, '')}
                  />
                </div>
              )
            }
            headerClassName="tableHeader font-normal"
            className="justify-content-center text-standard blackText tableCell printHide"
            footerClassName="tableFooter"
          />
          <Column
            headerClassName={`tableHeader`}
            className="tableCell p-0 printHide"
            footerClassName="tableFooter "
          />
        </Table>
      </div>
    );
  }
);

const transactionsAreEqual = (
  prevTransactions: Readonly<ClaimsTableProps>,
  nextTransactions: Readonly<ClaimsTableProps>
) => {
  return prevTransactions.claims === nextTransactions.claims;
};

export default React.memo(ClaimsTable, transactionsAreEqual);
